import { bnPageHead } from "@/lib/bn-head";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import {
  CaseBreakdown,
  CaseGallery,
  CaseHero,
  RelatedCases,
  caseStudyArticleSchema,
} from "@/components/case-study/CaseStudySections";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "@/content/site";
import { BN_CASES } from "@/content/bn/cases";
import { CASE_STUDY_REVIEWED_ON } from "@/content/case-study-media";

export const Route = createFileRoute("/bn/case-studies/$slug")({
  loader: ({ params }) => {
    const c = BN_CASES.find((x) => x.slug === params.slug);
    if (!c) throw notFound();
    return { c };
  },
  head: ({ loaderData }) => {
    const c = loaderData?.c;
    if (!c) return { meta: [{ title: "কেস স্টাডি পাওয়া যায়নি" }, { name: "robots", content: "noindex" }] };
    return bnPageHead({
      path: `/bn/case-studies/${c.slug}`,
      enPath: c.enPath,
      title: `${c.headline} — এসইও কেস স্টাডি`,
      // Summaries are shared evidence notes, so lead with the case-specific context.
      desc: `${c.headline} (${c.industry}, ${c.region})। ${c.summary}`,
      keywords: ["এসইও কেস স্টাডি", c.industry, c.region],
      type: "article",
      meta: [{ name: "author", content: SITE.fullName }],
      og: [
        { property: "article:published_time", content: CASE_STUDY_REVIEWED_ON },
        { property: "article:modified_time", content: CASE_STUDY_REVIEWED_ON },
      ],
    });
  },
  component: BnCaseDetail,
  notFoundComponent: () => (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-4xl">কেস স্টাডি পাওয়া যায়নি</h1>
      <Link to="/bn/case-studies" className="mt-6 inline-flex text-primary">← সব কেস স্টাডি</Link>
    </div>
  ),
});

function BnCaseDetail() {
  const { c } = Route.useLoaderData();
  const related = BN_CASES.filter((x) => x.slug !== c.slug).slice(0, 3);
  return (
    <>
      <Breadcrumbs
        items={[
          { name: "হোম", path: "/bn" },
          { name: "কেস স্টাডি", path: "/bn/case-studies" },
          { name: c.client, path: `/bn/case-studies/${c.slug}` },
        ]}
      />

      <JsonLd
        data={[
          caseStudyArticleSchema(c, "bn", c.enPath),
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "হোম", item: `${SITE.url}/bn` },
              { "@type": "ListItem", position: 2, name: "কেস স্টাডি", item: `${SITE.url}/bn/case-studies` },
              { "@type": "ListItem", position: 3, name: c.client, item: `${SITE.url}/bn/case-studies/${c.slug}` },
            ],
          },
        ]}
      />

      <CaseHero c={c} lang="bn" />
      <CaseGallery c={c} />
      <CaseBreakdown
        c={c}
        lang="bn"
        footer={
          <Link
            to="/case-studies/$slug"
            params={{ slug: c.slug }}
            className="mt-6 inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            English version <ArrowUpRight size={14} />
          </Link>
        }
      />
      <RelatedCases related={related} lang="bn" />

      <Section className="!pt-0">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/15 via-card to-card p-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl leading-snug">আপনার ব্যবসার জন্য পরিকল্পনা চান?</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            একটি সংক্ষিপ্ত ব্রিফ পাঠান — এক কর্মদিবসের মধ্যে উত্তর দেব।
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/bn/book" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-sm font-medium hover:opacity-90">
              কল বুক করুন <ArrowUpRight size={16} />
            </Link>
            <Link to="/bn/contact" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-medium hover:bg-card">
              যোগাযোগ
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
